// src/components/LogisticsDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Download, Users, MapPin, ChevronDown, ChevronUp, Truck, Package, CheckCircle, AlertCircle, X, Mail, Layers, Archive, BarChart3, ArrowUp } from 'lucide-react';
// X ya está importado
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { debugLog, apiLog, errorLog } from '../config/featureFlags';

const LogisticsDashboard = () => {
  const [logisticsData, setLogisticsData] = useState([]);
  const [trips, setTrips] = useState([]);
  const [envivos, setEnvivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);
  
  // Estado para botón "Volver arriba" y control de scroll
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(true);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // Filtros reutilizados de SearchModule
  const [filters, setFilters] = useState({
    tripId: '',
    clientName: '',
    envivoId: '',
    estadoArribo: '',
    estadoViaje: [], // Cambiado a array para múltiples selecciones
    startDate: '',
    endDate: '',
    exclusiveToEnvivo: false // Filtro para mostrar solo clientes exclusivos del envivo
  });

  // Estados para el filtro múltiple de Estado del Viaje (sección de filtros)
  const [showEstadoViajeFilter, setShowEstadoViajeFilter] = useState(false);
  const [estadoViajeOptions] = useState([
    { value: '', label: 'Estado inicial' },
    { value: 'barrido', label: 'Revisado' },
    { value: 'completo', label: 'Completo' },
    { value: 'notificado', label: 'Cliente notificado' },
    { value: 'alistar', label: 'Para alistar' },
    { value: 'preparado', label: 'Ya preparado' },
    { value: 'enviado', label: 'Enviado al cliente' },
    { value: 'abandonado', label: 'Abandonado' }
  ]);

  // Estados para el filtro local directo en la tabla
  const [showTableEstadoViajeFilter, setShowTableEstadoViajeFilter] = useState(false);
  const [tableEstadoViajeFilter, setTableEstadoViajeFilter] = useState([]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Funciones para manejar el filtro múltiple de Estado del Viaje
  const handleEstadoViajeToggle = (value) => {
    setFilters(prev => {
      const currentValues = prev.estadoViaje || [];
      const isSelected = currentValues.includes(value);
      
      if (isSelected) {
        // Remover el valor si ya está seleccionado
        return {
          ...prev,
          estadoViaje: currentValues.filter(v => v !== value)
        };
      } else {
        // Agregar el valor si no está seleccionado
        return {
          ...prev,
          estadoViaje: [...currentValues, value]
        };
      }
    });
  };

  const handleSelectAllEstadosViaje = () => {
    const allValues = estadoViajeOptions.map(option => option.value);
    setFilters(prev => ({
      ...prev,
      estadoViaje: allValues
    }));
  };

  const handleClearAllEstadosViaje = () => {
    setFilters(prev => ({
      ...prev,
      estadoViaje: []
    }));
  };

  // Funciones para el filtro local directo en la tabla
  const handleTableEstadoViajeToggle = (value) => {
    setTableEstadoViajeFilter(prev => {
      const isSelected = prev.includes(value);
      if (isSelected) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSelectAllTableEstadosViaje = () => {
    const allValues = estadoViajeOptions.map(option => option.value);
    setTableEstadoViajeFilter(allValues);
  };

  const handleClearAllTableEstadosViaje = () => {
    setTableEstadoViajeFilter([]);
  };

  // Función para hacer scroll a la sección de filtros (no utilizada actualmente)
  // const scrollToFilters = () => {
  //   const filtersSection = document.querySelector('.estado-viaje-filter');
  //   if (filtersSection) {
  //     filtersSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
  //     // Abrir el dropdown después del scroll
  //     setTimeout(() => {
  //       setShowEstadoViajeFilter(true);
  //     }, 500);
  //   }
  // };

  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      setTrips(data);
    } catch (err) {
      setError('Error al cargar viajes: ' + err.message);
    }
  };

  const loadEnvivos = useCallback(async (tripId = null) => {
    try {
      let data;
      if (tripId) {
        // ✅ OPTIMIZACIÓN: Solo cargar envivos del viaje seleccionado
        debugLog('🚀 Cargando envivos solo del viaje:', tripId);
        data = await apiService.envivo.getByTrip(tripId);
      } else {
        // Fallback: cargar todos si no hay tripId
        data = await apiService.envivo.getAll();
      }
      
      // ✅ VALIDACIÓN: Asegurar que data sea un array
      const envivosArray = Array.isArray(data) ? data : [];
      setEnvivos(envivosArray);
    } catch (err) {
      setError('Error al cargar en vivos: ' + err.message);
      setEnvivos([]); // Asegurar que siempre sea un array
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    loadTrips();
    // No cargar en vivos al inicio, esperar a que se seleccione un viaje
  }, []);

  // Cargar en vivos cuando cambie el viaje seleccionado
  useEffect(() => {
    if (filters.tripId && filters.tripId !== '' && filters.tripId !== 'Todos los viajes') {
      loadEnvivos(filters.tripId);
      // Limpiar selección de envivo cuando cambia el viaje
      setFilters(prev => ({ ...prev, envivoId: '' }));
    } else {
      // Si no hay viaje seleccionado, cargar todos los en vivos
      loadEnvivos();
    }
  }, [filters.tripId, loadEnvivos]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEstadoViajeFilter && !event.target.closest('.estado-viaje-filter')) {
        setShowEstadoViajeFilter(false);
      }
      if (showTableEstadoViajeFilter && !event.target.closest('.table-estado-viaje-filter')) {
        setShowTableEstadoViajeFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEstadoViajeFilter, showTableEstadoViajeFilter]);
  
  // Mostrar/ocultar botón "Volver arriba" y buscador móvil según scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobileOrTablet = window.innerWidth < 1024; // Mobile y tablet/iPad
      
      // Umbral para mostrar el botón y buscador: 200px desde el inicio
      const SHOW_THRESHOLD = 200;
      
      if (currentScrollY > SHOW_THRESHOLD) {
        setShowScrollTop(true);
        if (isMobileOrTablet) {
          setShowMobileSearch(true);
        } else {
          setShowMobileSearch(true);
        }
      } else {
        setShowScrollTop(false);
        if (isMobileOrTablet) {
          setShowMobileSearch(false);
        } else {
          setShowMobileSearch(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Función para limpiar resaltados anteriores
  const clearHighlights = useCallback(() => {
    const containers = document.querySelectorAll('.search-highlight-container');
    containers.forEach(container => {
      const text = container.textContent;
      const textNode = document.createTextNode(text);
      if (container.parentNode) {
        container.parentNode.replaceChild(textNode, container);
      }
    });
    document.body.normalize();
  }, []);

  // Función para resaltar texto en la página
  const highlightText = useCallback((term) => {
    if (!term || typeof window === 'undefined') return;
    
    clearHighlights();

    let windowFound = false;
    try {
      windowFound = window.find(term, false, false, true, false, true, false);
    } catch (e) {
      // Ignorar errores
    }

    if (!windowFound) {
      const allText = (document.body.innerText || document.body.textContent || '').toLowerCase();
      if (!allText.includes(term.toLowerCase())) {
        alert(`No se encontraron coincidencias para "${term}".`);
        return;
      }
    }

    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          if (
            parent.tagName === 'INPUT' || 
            parent.tagName === 'TEXTAREA' || 
            parent.tagName === 'SCRIPT' || 
            parent.tagName === 'STYLE' ||
            parent.closest('.search-highlight-container') ||
            parent.closest('nav') ||
            parent.closest('button')
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          
          if (node.textContent.trim().length === 0) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );

    const textNodes = [];
    let node;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    let found = false;
    
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];
      const text = textNode.textContent;
      
      regex.lastIndex = 0;
      if (regex.test(text)) {
        found = true;
        regex.lastIndex = 0;
        const highlightedText = text.replace(regex, '<mark class="search-highlight bg-yellow-300 text-gray-900 px-0.5 rounded">$1</mark>');
        
        const wrapper = document.createElement('span');
        wrapper.className = 'search-highlight-container';
        wrapper.innerHTML = highlightedText;
        
        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(wrapper, textNode);
        }
      }
    }

    if (windowFound && !found) {
      // El texto existe pero no lo encontramos en nodos de texto directos
    } else if (found) {
      try {
        window.find(term, false, false, true, false, true, false);
      } catch (e) {
        // Ignorar errores
      }
    }
  }, [clearHighlights]);

  const handleMobileQuickSearch = useCallback(() => {
    if (typeof window === 'undefined') return;
    const term = mobileSearchTerm.trim();
    if (!term) {
      clearHighlights();
      return;
    }

    highlightText(term);
  }, [mobileSearchTerm, highlightText, clearHighlights]);


  const handleSearch = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Determinar qué ID usar (tripId o envivoId)
      const tripId = (filters.tripId && filters.tripId !== '' && filters.tripId !== 'Todos los viajes') ? filters.tripId : null;
      const envivoId = (filters.envivoId && filters.envivoId !== '' && filters.envivoId !== 'Todos los en vivos') ? filters.envivoId : null;
      
      // Validar que si exclusiveToEnvivo está activo, también estén envivo_id y tripId
      if (filters.exclusiveToEnvivo) {
        if (!envivoId || !tripId) {
          setError('Para usar "Solo clientes exclusivos" debes seleccionar un envivo y un viaje');
          setLoading(false);
          return;
        }
      }
      
      // Filtrar filtros vacíos - NO incluir valores vacíos o "Todos los..."
      const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        // Manejo especial para estadoViaje (array)
        if (key === 'estadoViaje') {
          if (Array.isArray(value) && value.length > 0) {
            acc[key] = value;
          }
          return acc;
        }
        
        // Manejo especial para exclusiveToEnvivo (boolean)
        if (key === 'exclusiveToEnvivo') {
          if (value === true) {
            acc[key] = value;
          }
          return acc;
        }
        
        // No incluir filtros vacíos, null, undefined o valores por defecto
        if (value !== '' && value !== null && value !== undefined && value !== 'Todos los en vivos' && value !== 'Todos los viajes' && value !== 'Todos los estados') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      if (tripId || envivoId) {
        // Pasar los filtros correctos al servicio
        const searchFilters = {
          ...activeFilters,
          tripId: tripId,
          envivoId: envivoId
        };
        
        const data = await apiService.logistics.getControlReport(envivoId || tripId, searchFilters);
        
        // El backend devuelve { products: [...], statistics: {...} }
        // NO { data: [...], statistics: {...} }
        debugLog('📊 Respuesta completa del backend:', data);
        debugLog('📊 Products array:', data.products);
        debugLog('📊 Products length:', data.products?.length);
        debugLog('📊 Statistics:', data.statistics);
        
        const productsArray = data.products || [];
        debugLog('📊 Setting logisticsData with:', productsArray);
        debugLog('📊 Products array length:', productsArray.length);
        
        // Debug: Verificar si los productos tienen el campo carry_on
        if (productsArray.length > 0) {
          const firstProduct = productsArray[0];
          debugLog('🔍 Primer producto (ejemplo):', {
            product_name: firstProduct.product_name,
            carry_on: firstProduct.carry_on,
            carry_on_type: typeof firstProduct.carry_on,
            all_keys: Object.keys(firstProduct)
          });
          
          // Buscar productos con carry_on
          const productsWithCarryOn = productsArray.filter(p => 
            p.carry_on === 1 || p.carry_on === '1' || p.carry_on === true || p.carry_on === 'true'
          );
          debugLog('✈️ Productos con carry_on:', productsWithCarryOn.length);
          if (productsWithCarryOn.length > 0) {
            debugLog('✈️ Ejemplo de producto con carry_on:', {
              product_name: productsWithCarryOn[0].product_name,
              carry_on: productsWithCarryOn[0].carry_on,
              carry_on_type: typeof productsWithCarryOn[0].carry_on
            });
          }
        }
        
        setLogisticsData(productsArray);
        setStatistics(data.statistics || null);
      } else {
        setError('Debes seleccionar un viaje o en vivo específico para buscar');
      }
    } catch (err) {
      setError('Error al buscar: ' + err.message);
      errorLog('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Actualizar estado de UN producto específico (NUEVO)
  const handleUpdateProductStatus = async (orderItemId, newStatus, productName) => {
    try {
      debugLog('🔄 handleUpdateProductStatus llamado:', { orderItemId, newStatus, productName });
      debugLog('🔄 logisticsData actual:', logisticsData);
      
      // Obtener el estado anterior del producto antes de actualizar
      const productToUpdate = logisticsData.find(p => p.order_item_id === orderItemId);
      debugLog('🔄 Producto encontrado:', productToUpdate);
      
      const previousStatus = productToUpdate?.arrival_status || productToUpdate?.estado_de_arribo || 'pending';
      debugLog('🔄 Estado anterior:', previousStatus);
      
      const statusData = {
        arrival_status: newStatus,
        change_reason: `Actualización manual desde frontend - ${productName}`
      };

      debugLog('🔄 Llamando a API con:', { orderItemId, statusData });
      await apiService.logistics.updateProductStatus(orderItemId, statusData);
      debugLog('✅ API respondió exitosamente');
      
      // Actualizar el producto en logisticsData directamente
      // IMPORTANTE: Actualizar ambos campos arrival_status y estado_de_arribo
      setLogisticsData(prevData => {
        const updated = prevData.map(product => {
          if (product.order_item_id === orderItemId) {
            debugLog('🔄 Actualizando producto:', { 
              order_item_id: product.order_item_id, 
              arrival_status_anterior: product.arrival_status,
              estado_de_arribo_anterior: product.estado_de_arribo,
              nuevo_estado: newStatus
            });
            return { 
              ...product, 
              arrival_status: newStatus,
              estado_de_arribo: newStatus 
            };
          }
          return product;
        });
        debugLog('🔄 Datos actualizados:', updated);
        return updated;
      });
      
      // Actualizar el contador "Productos Arribaron" sin recargar todo
      setStatistics(prevStats => {
        if (!prevStats) return prevStats;
        
        const newStats = { ...prevStats };
        const wasArrived = previousStatus === 'arrived';
        const isArrived = newStatus === 'arrived';
        
        // Si el estado cambió de no-arrived a arrived, incrementar
        // Si el estado cambió de arrived a no-arrived, decrementar
        if (wasArrived && !isArrived) {
          // Decrementar contador
          if (newStats.estados_arribo) {
            newStats.estados_arribo = {
              ...newStats.estados_arribo,
              arrived: Math.max(0, (newStats.estados_arribo.arrived || 0) - 1)
            };
          } else {
            newStats.arrived = Math.max(0, (newStats.arrived || 0) - 1);
          }
        } else if (!wasArrived && isArrived) {
          // Incrementar contador
          if (newStats.estados_arribo) {
            newStats.estados_arribo = {
              ...newStats.estados_arribo,
              arrived: (newStats.estados_arribo.arrived || 0) + 1
            };
          } else {
            newStats.arrived = (newStats.arrived || 0) + 1;
          }
        }
        
        return newStats;
      });
      
      setError('');
      debugLog('✅ Estado actualizado exitosamente en el frontend');
    } catch (err) {
      setError('Error al actualizar estado del producto: ' + err.message);
      errorLog('Update product status error:', err);
    }
  };

  // Actualizar estado de TODA la orden (LEGACY - no utilizada actualmente)
  // const handleUpdateOrderStatus = async (orderId, newStatus, clientName) => {
  //   try {
  //     const statusData = {
  //       arrival_status: newStatus,
  //       change_reason: `Actualización manual de orden completa - ${clientName}`
  //     };

  //     await apiService.logistics.updateOrderStatus(orderId, statusData);
      
  //     // Recargar datos después de la actualización
  //     await handleSearch();
  //   } catch (err) {
  //     setError('Error al actualizar estado de la orden: ' + err.message);
  //     console.error('Update order status error:', err);
  //   }
  // };

  // Función para formatear fecha en horario de Colombia (YYYY-MM-DD HH:MM:SS AM/PM)
  const formatDateColombia = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      // Usar Intl.DateTimeFormat para obtener componentes en horario de Colombia
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Bogota',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      
      // Obtener partes de la fecha
      const parts = formatter.formatToParts(date);
      
      const year = parts.find(p => p.type === 'year').value;
      const month = parts.find(p => p.type === 'month').value;
      const day = parts.find(p => p.type === 'day').value;
      const hour = parts.find(p => p.type === 'hour').value;
      const minute = parts.find(p => p.type === 'minute').value;
      const second = parts.find(p => p.type === 'second').value;
      const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || '';
      
      // Formatear como YYYY-MM-DD HH:MM:SS AM/PM
      return `${year}-${month}-${day} ${hour}:${minute}:${second} ${dayPeriod.toUpperCase()}`;
    } catch (error) {
      errorLog('Error formateando fecha:', error);
      return null;
    }
  };

  // NUEVO: Actualizar delivery_status de TODAS las órdenes de un envivo
  // CORREGIDO: Solo enviar UNA petición por orden (no por producto)
  const handleUpdateEnvivoDeliveryStatus = async (envivoId, envivoName, orderIds, newDeliveryStatus) => {
    try {
      apiLog(`🚀 Actualizando delivery_status para envivo: ${envivoName} (ID: ${envivoId}), estado: ${newDeliveryStatus}`);
      apiLog(`📋 Órdenes a actualizar:`, orderIds);
      
      // Obtener order_ids únicos (varios productos pueden pertenecer a la misma orden)
      const uniqueOrderIds = [...new Set(orderIds)];
      apiLog(`📋 Órdenes únicas a actualizar: ${uniqueOrderIds.length} (de ${orderIds.length} productos)`);
      
      // Actualizar solo las órdenes únicas (UNA petición por orden)
      const updatePromises = uniqueOrderIds.map(orderId => 
        apiService.order.updateDeliveryStatus(orderId, newDeliveryStatus)
      );
      
      const results = await Promise.all(updatePromises);
      apiLog('✅ Resultado de las actualizaciones:', results);
      
      // Mostrar mensaje de éxito
      setError(''); // Limpiar errores previos
      alert(`✅ Actualización exitosa!\n\nSe actualizaron ${uniqueOrderIds.length} orden(es) del envivo "${envivoName}"\nEstado: ${newDeliveryStatus}`);
      
      // Recargar datos después de la actualización para reflejar la nueva fecha
      await handleSearch();
    } catch (err) {
      const errorMessage = err.message || 'Error desconocido';
      setError(`Error al actualizar estado de entrega: ${errorMessage}`);
      errorLog('Update envivo delivery status error:', err);
      alert(`❌ Error al actualizar estado de entrega:\n\n${errorMessage}`);
    }
  };

  // MANTENER: Función anterior para compatibilidad (actualiza todas las órdenes de un cliente en un viaje)
  // eslint-disable-next-line no-unused-vars
  const handleUpdateClientTripDelivery = async (clientUsername, tripId, newDeliveryStatus) => {
    try {
      apiLog(`🚀 Actualizando delivery_status para cliente: ${clientUsername}, viaje: ${tripId}, estado: ${newDeliveryStatus}`);
      
      const result = await apiService.logistics.updateClientTripDelivery(clientUsername, tripId, newDeliveryStatus);
      
      apiLog('✅ Resultado de la actualización:', result);
      
      // Verificar que los cambios se realizaron en las tablas dev_*
      await verifyDevTableChanges(clientUsername, tripId, newDeliveryStatus, result.order_ids || []);
      
      // Mostrar mensaje de éxito
      alert(`✅ Actualización exitosa!\n\n${result.message}\n\nÓrdenes afectadas: ${result.affected_orders}\nEstado: ${result.delivery_status}`);
      
      // Recargar datos después de la actualización
      await handleSearch();
    } catch (err) {
      const errorMessage = err.message || 'Error desconocido';
      setError(`Error al actualizar estado de entrega: ${errorMessage}`);
      errorLog('Update client trip delivery error:', err);
      alert(`❌ Error al actualizar estado de entrega:\n\n${errorMessage}`);
    }
  };

  // Función para verificar que los cambios se realizaron en las tablas dev_*
  const verifyDevTableChanges = async (clientUsername, tripId, expectedStatus, orderIds) => {
    try {
      debugLog('🔍 VERIFICANDO CAMBIOS EN TABLAS dev_*');
      debugLog('   - Cliente:', clientUsername);
      debugLog('   - Viaje:', tripId);
      debugLog('   - Estado esperado:', expectedStatus);
      debugLog('   - Órdenes a verificar:', orderIds);
      
      // Usar getControlReport para verificar el estado actual
      const filters = {
        tripId: tripId.toString(),
        clientName: clientUsername
      };
      
      const verificationData = await apiService.logistics.getControlReport(tripId.toString(), filters);
      
      if (!verificationData || !verificationData.products) {
        debugLog('⚠️ No se pudieron obtener datos para verificación');
        return;
      }
      
      // Filtrar productos/órdenes del cliente específico
      const clientOrders = verificationData.products.filter(product => 
        product.client_username === clientUsername && 
        orderIds.includes(product.order_id)
      );
      
      // Verificar que todas las órdenes tengan el estado esperado
      const ordersWithCorrectStatus = clientOrders.filter(order => 
        order.delivery_status === expectedStatus
      );
      
      const verificationResult = {
        totalOrders: clientOrders.length,
        ordersWithCorrectStatus: ordersWithCorrectStatus.length,
        ordersWithIncorrectStatus: clientOrders.length - ordersWithCorrectStatus.length,
        orderDetails: clientOrders.map(order => ({
          order_id: order.order_id,
          client_username: order.client_username,
          delivery_status: order.delivery_status,
          expected_status: expectedStatus,
          status_match: order.delivery_status === expectedStatus
        }))
      };
      
      debugLog('📊 RESULTADO DE VERIFICACIÓN:');
      debugLog('   - Total de órdenes verificadas:', verificationResult.totalOrders);
      debugLog('   - Órdenes con estado correcto:', verificationResult.ordersWithCorrectStatus);
      debugLog('   - Órdenes con estado incorrecto:', verificationResult.ordersWithIncorrectStatus);
      debugLog('   - Detalles por orden:', verificationResult.orderDetails);
      
      // Mostrar resultado detallado
      if (verificationResult.ordersWithCorrectStatus === verificationResult.totalOrders && verificationResult.totalOrders > 0) {
        debugLog('✅ VERIFICACIÓN EXITOSA: Todas las órdenes tienen el estado correcto en las tablas dev_*');
        debugLog('   - El backend está usando correctamente las tablas dev_*');
        debugLog('   - El header X-App-Environment: development está funcionando correctamente');
      } else if (verificationResult.totalOrders === 0) {
        debugLog('⚠️ ADVERTENCIA: No se encontraron órdenes para verificar');
        debugLog('   - Esto puede indicar que el backend no está usando las tablas dev_*');
        debugLog('   - O que las órdenes no existen en las tablas dev_*');
      } else {
        errorLog('❌ ERROR: Algunas órdenes no tienen el estado correcto');
        errorLog('   - Verificar que el backend esté usando las tablas dev_*');
        errorLog('   - Verificar que el header X-App-Environment: development se esté enviando');
      }
      
      return verificationResult;
    } catch (error) {
      errorLog('❌ Error verificando cambios en tablas dev_*:', error);
      errorLog('   - No se pudo verificar que los cambios se realizaron en las tablas dev_*');
      errorLog('   - Ejecuta el script SQL manualmente para verificar');
      return null;
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Colores de fondo para cada cliente (usando esquema de colores de la página)
  const clientBackgroundColors = [
    'bg-purple-50',      // Cliente 1: Púrpura claro
    'bg-blue-50',        // Cliente 2: Azul claro
    'bg-pink-50',        // Cliente 3: Rosa claro
    'bg-indigo-50',      // Cliente 4: Índigo claro
    'bg-teal-50',        // Cliente 5: Teal claro
    'bg-cyan-50',        // Cliente 6: Cian claro
    'bg-violet-50',      // Cliente 7: Violeta claro
    'bg-fuchsia-50',     // Cliente 8: Fucsia claro
  ];

  const clientBorderColors = [
    'border-purple-200',
    'border-blue-200',
    'border-pink-200',
    'border-indigo-200',
    'border-teal-200',
    'border-cyan-200',
    'border-violet-200',
    'border-fuchsia-200',
  ];

  // Agrupar productos por cliente y luego por envivo para la visualización jerárquica
  const groupedData = React.useMemo(() => {
    debugLog('🔄 groupedData useMemo triggered with logisticsData:', logisticsData);
    debugLog('🔄 logisticsData type:', typeof logisticsData);
    debugLog('🔄 logisticsData isArray:', Array.isArray(logisticsData));
    debugLog('🔄 logisticsData length:', logisticsData?.length);
    
    if (!logisticsData || !Array.isArray(logisticsData)) {
      debugLog('🔄 Returning empty array - no valid logisticsData');
      return [];
    }
    
    // Primero agrupar por cliente, luego por envivo dentro de cada cliente
    const clientGroups = {};
    
    logisticsData.forEach((product, index) => {
      debugLog(`🔄 Processing product ${index}:`, product);
      const clientKey = product.client_username || 'Sin cliente';
      
      // Inicializar cliente si no existe
      if (!clientGroups[clientKey]) {
        clientGroups[clientKey] = {
          cliente: clientKey,
          estado_del_viaje: product.delivery_status || '', // Estado del viaje (mismo para todos los productos del cliente)
          total_amount: '0.00',
          envivos: {} // Agrupar por envivo dentro del cliente
        };
      }
      
      const envivoKey = product.envivo_name || 'Sin envivo';
      const envivoId = product.envivo_id || null;
      
      // Inicializar envivo si no existe dentro del cliente
      if (!clientGroups[clientKey].envivos[envivoKey]) {
        clientGroups[clientKey].envivos[envivoKey] = {
          envivo_name: envivoKey,
          envivo_id: envivoId,
          delivery_status: product.delivery_status || '', // Estado del viaje por envivo
          last_delivery_status_update: product.last_delivery_status_update || null, // Fecha de última actualización
          productos: []
        };
      } else {
        // Si el envivo ya existe, actualizar last_delivery_status_update si este producto tiene una fecha más reciente
        const existingDate = clientGroups[clientKey].envivos[envivoKey].last_delivery_status_update;
        const currentDate = product.last_delivery_status_update;
        
        if (currentDate && (!existingDate || new Date(currentDate) > new Date(existingDate))) {
          clientGroups[clientKey].envivos[envivoKey].last_delivery_status_update = currentDate;
        }
      }
      
      // Agregar producto al envivo correspondiente
      // Normalizar carry_on: puede venir como número, string, boolean o null/undefined
      const carryOnValue = product.carry_on === 1 || 
                          product.carry_on === '1' || 
                          product.carry_on === true || 
                          product.carry_on === 'true' ? 1 : 0;
      
      debugLog('🔍 Producto carry_on:', {
        product_name: product.product_name,
        carry_on_raw: product.carry_on,
        carry_on_type: typeof product.carry_on,
        carry_on_normalized: carryOnValue
      });
      
      clientGroups[clientKey].envivos[envivoKey].productos.push({
        order_item_id: product.order_item_id,
        order_id: product.order_id,
        producto: product.product_name,
        cantidad: product.quantity,
        estado_de_arribo: product.arrival_status,
        price: product.price,
        envivo_name: product.envivo_name || '',
        envivo_id: product.envivo_id || null,
        delivery_status: product.delivery_status || '',
        carry_on: carryOnValue // Campo normalizado para indicar si viaja en avión
      });
      
      // Sumar al total del cliente
      const currentTotal = parseFloat(clientGroups[clientKey].total_amount || 0);
      const productTotal = parseFloat(product.price || 0) * (product.quantity || 1);
      clientGroups[clientKey].total_amount = (currentTotal + productTotal).toFixed(2);
    });
    
    // Convertir a array plano con estructura jerárquica para el renderizado
    const result = Object.values(clientGroups).map((clientGroup, clientIndex) => ({
      ...clientGroup,
      clientIndex: clientIndex % clientBackgroundColors.length, // Índice para colores
      envivosArray: Object.values(clientGroup.envivos) // Convertir envivos a array
    }));
    
    debugLog('🔄 Final groupedData result:', result);
    debugLog('🔄 Final groupedData length:', result.length);
    
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logisticsData]);

  const sortedData = React.useMemo(() => {
    let filteredData = groupedData;

    // Debug: Mostrar información del filtrado
    debugLog('🔍 Debug filtrado local:', {
      groupedDataLength: groupedData.length,
      tableEstadoViajeFilter,
      filterLength: tableEstadoViajeFilter.length
    });

    // Aplicar filtro local de estado del viaje si hay selecciones
    if (tableEstadoViajeFilter.length > 0) {
      // Filtrar a nivel de envivo, no solo a nivel de cliente
      filteredData = groupedData
        .map(clientItem => {
          const filteredEnvivos = (clientItem.envivosArray || []).filter(envivo =>
            tableEstadoViajeFilter.includes(envivo.delivery_status || '')
          );

          // Debug por cliente
          debugLog('🔍 Filtro por cliente/envivo:', {
            cliente: clientItem.cliente,
            estadosSeleccionados: tableEstadoViajeFilter,
            envivosOriginales: (clientItem.envivosArray || []).map(e => e.delivery_status),
            envivosFiltrados: filteredEnvivos.map(e => e.delivery_status)
          });

          return {
            ...clientItem,
            envivosArray: filteredEnvivos
          };
        })
        // Mantener solo clientes que tienen al menos un envivo después del filtro
        .filter(clientItem => (clientItem.envivosArray || []).length > 0);

      debugLog('🔍 Resultado filtrado por estados de viaje:', {
        originalClientes: groupedData.length,
        clientesConEnvivosFiltrados: filteredData.length
      });
    }

    // Aplicar ordenamiento
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toString().toLowerCase();
      const bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      
      // Ordenamiento alfabético
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groupedData, sortConfig, tableEstadoViajeFilter]);

  const exportToExcel = () => {
    if (!logisticsData.length) {
      setError('No hay datos para exportar');
      return;
    }

    // Preparar datos para Excel con la nueva estructura jerárquica
    const excelData = [];
    
    // Usar groupedData que ya tiene la estructura jerárquica (cliente -> envivo -> productos)
    groupedData.forEach(clientItem => {
      if (clientItem.envivosArray && clientItem.envivosArray.length > 0) {
        clientItem.envivosArray.forEach((envivo, envivoIndex) => {
          if (envivo.productos && envivo.productos.length > 0) {
            envivo.productos.forEach((producto, productoIndex) => {
              excelData.push({
                'cliente': productoIndex === 0 && envivoIndex === 0 ? clientItem.cliente || '' : '',
                'envivo': productoIndex === 0 ? envivo.envivo_name || '' : '',
                'productos': producto.producto || '',
                'cantidad': producto.cantidad || 1,
                'precio': parseFloat(producto.price || 0).toFixed(2),
                'estado de arribo': producto.estado_de_arribo || '',
                'estado del viaje': productoIndex === 0 && envivoIndex === 0 ? clientItem.estado_del_viaje || '' : ''
              });
            });
          }
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Control Logístico');
    
    const targetId = filters.tripId || filters.envivoId;
    const fileName = `control_logistico_${targetId}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };


  const getStatusColor = (status) => {
    switch (status) {
      // Estados de Arribo (no cambiaron)
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'arrived':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'missing':
        return 'bg-red-100 text-red-800 border-red-200';
      
      // Estados del Viaje (actualizados)
      case '': // Sin Estado
        return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'barrido': // Barrido
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completo': // Completo
        return 'bg-green-100 text-green-800 border-green-200';
      case 'notificado': // Notificado
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'alistar': // Alistar
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'preparado': // Preparado
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'enviado': // Enviado
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'abandonado': // Abandonado
        return 'bg-red-100 text-red-800 border-red-200';
      
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para obtener colores más intensos para los selects del estado del viaje
  const getDeliveryStatusSelectColor = (status) => {
    switch (status) {
      case '': // Sin Estado
        return 'bg-gray-200 text-gray-800 border-gray-300';
      case 'barrido': // Barrido
        return 'bg-blue-300 text-blue-900 border-blue-400';
      case 'completo': // Completo
        return 'bg-green-300 text-green-900 border-green-400';
      case 'notificado': // Notificado
        return 'bg-purple-300 text-purple-900 border-purple-400';
      case 'alistar': // Alistar
        return 'bg-orange-300 text-orange-900 border-orange-400';
      case 'preparado': // Preparado
        return 'bg-indigo-300 text-indigo-900 border-indigo-400';
      case 'enviado': // Enviado
        return 'bg-teal-300 text-teal-900 border-teal-400';
      case 'abandonado': // Abandonado
        return 'bg-red-300 text-red-900 border-red-400';
      
      default:
        return 'bg-gray-200 text-gray-800 border-gray-300';
    }
  };


  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Truck className="w-6 h-6 text-blue-600" />
          Control Logístico
        </h1>
        <p className="text-gray-600 mt-1">Gestión de estados de arribo y viaje por en vivo</p>
      </div>

      {/* Filtros de Búsqueda - Mismo diseño que SearchModule */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filtros de Búsqueda</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Viaje */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Viaje
            </label>
            <select
              value={filters.tripId}
              onChange={(e) => setFilters({...filters, tripId: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los viajes</option>
              {trips.map((trip) => {
                // Limpiar el guion final del nombre del viaje
                const tripName = trip.name?.replace(/-$/, '') || trip.name || '';
                // Mostrar fechas si existen
                const dateRange = trip.startDate && trip.endDate 
                  ? ` - ${new Date(trip.startDate).toLocaleDateString('es-CO')} - ${new Date(trip.endDate).toLocaleDateString('es-CO')}`
                  : '';
                return (
                  <option key={trip.id} value={trip.id}>
                    {tripName}{dateRange}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Cliente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cliente (Nombre/Username)
            </label>
            <input
              type="text"
              placeholder="Nombre o username de Instagram"
              value={filters.clientName}
              onChange={(e) => setFilters({...filters, clientName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* En Vivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              En Vivo
            </label>
            <select
              value={filters.envivoId}
              onChange={(e) => {
                const newEnvivoId = e.target.value;
                setFilters({
                  ...filters, 
                  envivoId: newEnvivoId,
                  // Desactivar exclusiveToEnvivo si no hay envivo seleccionado
                  exclusiveToEnvivo: newEnvivoId ? filters.exclusiveToEnvivo : false
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los en vivos</option>
              {envivos.map((envivo) => (
                <option key={envivo.id} value={envivo.id}>
                  {envivo.name}
                </option>
              ))}
            </select>
            
            {/* Checkbox para filtro exclusivo - Siempre visible */}
            <div className="mt-2">
              <label className={`flex items-center gap-2 ${filters.envivoId && filters.envivoId !== '' && filters.envivoId !== 'Todos los en vivos' ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                <input
                  type="checkbox"
                  checked={filters.exclusiveToEnvivo}
                  disabled={!filters.envivoId || filters.envivoId === '' || filters.envivoId === 'Todos los en vivos'}
                  onChange={(e) => {
                    if (filters.envivoId && filters.envivoId !== '' && filters.envivoId !== 'Todos los en vivos') {
                      setFilters({...filters, exclusiveToEnvivo: e.target.checked});
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">
                  Solo clientes exclusivos de este envivo
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                {filters.envivoId && filters.envivoId !== '' && filters.envivoId !== 'Todos los en vivos' 
                  ? 'Mostrar solo clientes que tienen pedidos en este envivo y no en otros del mismo viaje'
                  : 'Selecciona un envivo para habilitar este filtro'
                }
              </p>
            </div>
          </div>

          {/* Estado de Arribo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado de Arribo
            </label>
            <select
              value={filters.estadoArribo}
              onChange={(e) => setFilters({...filters, estadoArribo: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="arrived">Arribó</option>
              <option value="missing">Faltante</option>
            </select>
          </div>

          {/* Estado del Viaje - Filtro Múltiple */}
          <div className="relative estado-viaje-filter">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <div className="flex items-center gap-2">
                Estado del Viaje
                {filters.estadoViaje.length > 0 && (
                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                    {filters.estadoViaje.length} filtrado{filters.estadoViaje.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </label>
            <button
              type="button"
              onClick={() => setShowEstadoViajeFilter(!showEstadoViajeFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
            >
              <span className="text-gray-700">
                {filters.estadoViaje.length === 0 
                  ? 'Todos los estados' 
                  : filters.estadoViaje.length === 1
                    ? estadoViajeOptions.find(opt => opt.value === filters.estadoViaje[0])?.label
                    : `${filters.estadoViaje.length} estados seleccionados`
                }
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showEstadoViajeFilter ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown de opciones múltiples */}
            {showEstadoViajeFilter && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                <div className="p-2 border-b border-gray-200">
                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAllEstadosViaje}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      Seleccionar Todos
                    </button>
                    <button
                      onClick={handleClearAllEstadosViaje}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Limpiar
                    </button>
                  </div>
                </div>
                {estadoViajeOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters.estadoViaje.includes(option.value)}
                      onChange={() => handleEstadoViajeToggle(option.value)}
                      className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Fecha Inicio */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Fecha Fin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setFilters({
              tripId: '',
              clientName: '',
              envivoId: '',
              estadoArribo: '',
              estadoViaje: [], // Limpiar array
              startDate: '',
              endDate: '',
              exclusiveToEnvivo: false
            })}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Limpiar Filtros
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar Excel
            </button>
            
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Search className="w-4 h-4" />
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>
        </div>
      </div>


      {/* Estadísticas */}
      {/* Badge para indicar filtro exclusivo activo */}
      {filters.exclusiveToEnvivo && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Mostrando solo clientes exclusivos de este envivo
            </span>
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full font-semibold">
              Filtro activo
            </span>
          </div>
          <p className="text-xs text-blue-700 mt-1 ml-7">
            Solo se muestran clientes que tienen pedidos en este envivo y no en otros envivos del mismo viaje
          </p>
        </div>
      )}

      {(statistics || groupedData.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Clientes</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.total_clientes || groupedData.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Productos Arribaron</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.arrived || statistics?.estados_arribo?.arrived || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Órdenes Barridas</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.barrido || statistics?.estados_viaje?.barrido || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Órdenes Completas</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.completo || statistics?.estados_viaje?.completo || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Clientes Notificados</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.notificado || statistics?.estados_viaje?.notificado || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Layers className="w-8 h-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Para Alistar</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.alistar || statistics?.estados_viaje?.alistar || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Archive className="w-8 h-8 text-indigo-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Ya Preparados</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.preparado || statistics?.estados_viaje?.preparado || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <Truck className="w-8 h-8 text-teal-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Enviados</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.enviado || statistics?.estados_viaje?.enviado || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-gray-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Productos</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.total_products || logisticsData.length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabla de Control Logístico */}
      {(() => {
        debugLog('🎯 Render condition check:');
        debugLog('🎯 logisticsData:', logisticsData);
        debugLog('🎯 logisticsData.length:', logisticsData?.length);
        debugLog('🎯 groupedData:', groupedData);
        debugLog('🎯 groupedData.length:', groupedData?.length);
        debugLog('🎯 Should show table:', logisticsData?.length > 0);
        return logisticsData?.length > 0;
      })() ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Control Logístico
            </h2>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Exportar Excel
            </button>
          </div>

          {/* Información sobre la funcionalidad */}
          {/*
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  🚀 Nueva Funcionalidad: Actualización por Cliente y Viaje
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p className="mb-2">
                    <strong>Estado del Viaje:</strong> Al cambiar este estado, se actualizarán <strong>TODAS las órdenes</strong> de ese cliente en el viaje actual con una sola petición.
                  </p>
                  <p className="text-xs">
                    💡 <strong>Ejemplo:</strong> Si el cliente "anitalahuerfanita" tiene 3 órdenes en el viaje "Octubre 2025", 
                    al cambiar el estado a "Enviado" se actualizarán las 3 órdenes automáticamente.
                  </p>
                </div>
              </div>
            </div>
          </div>
          */}
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => handleSort('cliente')}
                    title="Clic para ordenar alfabéticamente (mantiene productos agrupados)"
                  >
                    <div className="flex items-center gap-1">
                      Cliente
                      {sortConfig.key === 'cliente' ? (
                        sortConfig.direction === 'asc' ? 
                          <ChevronUp className="w-4 h-4 text-blue-600" /> : 
                          <ChevronDown className="w-4 h-4 text-blue-600" />
                      ) : (
                        <div className="flex flex-col opacity-30">
                          <ChevronUp className="w-3 h-3 -mb-1" />
                          <ChevronDown className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => handleSort('envivo')}
                    title="Clic para ordenar alfabéticamente (mantiene productos agrupados)"
                  >
                    <div className="flex items-center gap-1">
                      En Vivo
                      {sortConfig.key === 'envivo' ? (
                        sortConfig.direction === 'asc' ? 
                          <ChevronUp className="w-4 h-4 text-blue-600" /> : 
                          <ChevronDown className="w-4 h-4 text-blue-600" />
                      ) : (
                        <div className="flex flex-col opacity-30">
                          <ChevronUp className="w-3 h-3 -mb-1" />
                          <ChevronDown className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Productos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cantidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado de Arribo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-100">
                    <div className="flex items-center gap-1">
                      Estado del En Vivo
                      <div className="relative table-estado-viaje-filter">
                        <Filter 
                          className="w-3 h-3 text-orange-600 cursor-pointer hover:text-orange-800 transition-colors" 
                          title="Clic para filtrar estados del viaje"
                          onClick={() => setShowTableEstadoViajeFilter(!showTableEstadoViajeFilter)}
                        />
                        {tableEstadoViajeFilter.length > 0 && (
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full" title={`${tableEstadoViajeFilter.length} estados filtrados`}></div>
                        )}
                        
                        {/* Menú de filtro directo en la tabla */}
                        {showTableEstadoViajeFilter && (
                          <div className="absolute z-20 mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg right-0">
                            <div className="p-2 border-b border-gray-200">
                              <div className="flex gap-2">
                                <button
                                  onClick={handleSelectAllTableEstadosViaje}
                                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                >
                                  Seleccionar Todos
                                </button>
                                <button
                                  onClick={handleClearAllTableEstadosViaje}
                                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                >
                                  Limpiar
                                </button>
                              </div>
                            </div>
                            <div className="max-h-48 overflow-auto">
                              {estadoViajeOptions.map((option) => (
                                <label
                                  key={option.value}
                                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={tableEstadoViajeFilter.includes(option.value)}
                                    onChange={() => handleTableEstadoViajeToggle(option.value)}
                                    className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                  />
                                  <span className="text-sm text-gray-700">{option.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {sortedData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {tableEstadoViajeFilter.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-lg font-medium">No hay resultados para los estados seleccionados</p>
                          <p className="text-sm">
                            Estados seleccionados: <span className="font-medium">{tableEstadoViajeFilter.join(', ')}</span>
                          </p>
                          <button
                            onClick={handleClearAllTableEstadosViaje}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          >
                            Limpiar filtros
                          </button>
                        </div>
                      ) : (
                        <p className="text-lg font-medium">No hay datos disponibles</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  sortedData.map((clientItem, clientIndex) => {
                    const clientBgColor = clientBackgroundColors[clientItem.clientIndex || (clientIndex % clientBackgroundColors.length)];
                    const clientBorderColor = clientBorderColors[clientItem.clientIndex || (clientIndex % clientBorderColors.length)];
                    
                    // Calcular índices acumulados para determinar primera fila
                    let globalProductIndex = 0;
                    
                    return (
                      <React.Fragment key={`client-${clientItem.cliente}-${clientIndex}`}>
                        {clientItem.envivosArray && clientItem.envivosArray.length > 0 ? (
                          clientItem.envivosArray.map((envivo, envivoIndex) => {
                            return envivo.productos && envivo.productos.length > 0 ? (
                              envivo.productos.map((producto, productoIndex) => {
                                const isFirstProductOfEnvivo = productoIndex === 0;
                                const isFirstProductOfClient = globalProductIndex === 0;
                                globalProductIndex++;
                                
                                return (
                                  <tr 
                                    key={`${producto.order_item_id}-${envivoIndex}-${productoIndex}`}
                                    className={`border-l-4 ${clientBorderColor} ${clientBgColor} hover:opacity-90 transition-opacity ${isFirstProductOfClient ? 'border-t-4 border-t-gray-600' : 'border-t border-t-gray-200'}`}
                                  >
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isFirstProductOfClient ? 'text-gray-900 font-semibold' : ''}`}>
                                      {isFirstProductOfClient ? (clientItem.cliente || '') : ''}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isFirstProductOfEnvivo ? 'text-gray-900 font-medium' : ''}`}>
                                      {isFirstProductOfEnvivo ? (envivo.envivo_name || '') : ''}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                      <div className="flex items-center gap-2">
                                        {(producto.carry_on === 1 || 
                                          producto.carry_on === '1' || 
                                          producto.carry_on === true || 
                                          producto.carry_on === 'true') && (
                                          <span className="text-blue-600 text-lg" title="Viaja en maleta (avión)">
                                            ✈️
                                          </span>
                                        )}
                                        <span>{producto.producto || producto.nombre || producto}</span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {producto.cantidad || 1}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <select
                                        value={producto.estado_de_arribo || 'pending'}
                                        onChange={(e) => handleUpdateProductStatus(
                                          producto.order_item_id, 
                                          e.target.value, 
                                          producto.producto || producto.nombre || producto
                                        )}
                                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(producto.estado_de_arribo)} focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white`}
                                      >
                                        <option value="pending">Pendiente</option>
                                        <option value="arrived">Arribó</option>
                                        <option value="missing">Faltante</option>
                                      </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap bg-orange-50">
                                      {isFirstProductOfEnvivo ? (
                                        <div className="flex flex-col gap-1">
                                          <select
                                            value={envivo.delivery_status || ''}
                                            onChange={(e) => {
                                              // Obtener todos los order_ids del envivo
                                              const orderIds = envivo.productos.map(p => p.order_id);
                                              handleUpdateEnvivoDeliveryStatus(
                                                envivo.envivo_id,
                                                envivo.envivo_name,
                                                orderIds,
                                                e.target.value
                                              );
                                            }}
                                            className={`px-2 py-1 text-xs font-medium rounded-full border ${getDeliveryStatusSelectColor(envivo.delivery_status)} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                            title={`Actualizar estado de entrega de todas las órdenes del envivo "${envivo.envivo_name}"`}
                                          >
                                            {estadoViajeOptions.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                          {envivo.last_delivery_status_update && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              {formatDateColombia(envivo.last_delivery_status_update)}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getDeliveryStatusSelectColor(envivo.delivery_status)}`}>
                                            {estadoViajeOptions.find(opt => opt.value === envivo.delivery_status)?.label || 'Sin estado'}
                                          </span>
                                          {envivo.last_delivery_status_update && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              {formatDateColombia(envivo.last_delivery_status_update)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : null;
                          })
                        ) : (
                          <tr className={`border-l-4 ${clientBorderColor} ${clientBgColor}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {clientItem.cliente || ''}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              -
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              Sin productos
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              -
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-800 border-gray-200">
                                Sin estado
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap bg-orange-50">
                              <span className="px-2 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-800 border-gray-200">
                                Sin estado
                              </span>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (filters.tripId || filters.envivoId) ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos de logística</h3>
          <p className="text-gray-500">No se encontraron registros para este viaje.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Selecciona un viaje</h3>
          <p className="text-gray-500">Elige un viaje para ver el control logístico.</p>
        </div>
      )}

      {/* Botón "Ir Arriba" de la derecha (solo escritorio lg) */}
      <button
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className="hidden lg:flex fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 z-50 items-center justify-center hover:scale-110"
        title="Volver arriba"
      >
        <ArrowUp size={24} />
      </button>

      {/* Buscador y botón "Ir Arriba" para iPad y Mobile (md hasta lg) */}
      <div className={`md:flex lg:hidden fixed bottom-4 right-4 z-50 flex items-center gap-3 transition-all duration-300 ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        {/* Campo de búsqueda expandible (se expande hacia la izquierda cuando se abre) */}
        <div className={`flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden transition-all duration-300 ${
          isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}>
          {/* Botón X para cerrar (a la izquierda del campo) */}
          {isSearchExpanded && (
            <button
              type="button"
              onClick={() => {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              title="Cerrar búsqueda"
            >
              <X size={20} className="font-bold" strokeWidth={3} />
            </button>
          )}
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
              if (event.key === 'Escape') {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onBlur={() => {
              // Cerrar si está vacío al perder el foco
              if (!mobileSearchTerm.trim()) {
                setIsSearchExpanded(false);
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            placeholder="Buscar en la página..."
            className={`px-4 py-3 text-sm text-gray-700 outline-none transition-all ${
              isSearchExpanded ? 'w-full flex-1' : 'w-0 hidden'
            }`}
            aria-label="Buscar en la página"
            autoFocus={isSearchExpanded}
          />
          {isSearchExpanded && (
            <button
              type="button"
              onClick={handleMobileQuickSearch}
              className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
              title="Buscar coincidencia"
            >
              <Search size={20} />
            </button>
          )}
        </div>

        {/* Botón de lupa (solo visible cuando NO está expandido) */}
        <button
          type="button"
          onClick={() => setIsSearchExpanded(!isSearchExpanded)}
          className={`bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50 ${
            isSearchExpanded ? 'hidden' : 'flex'
          }`}
          title="Buscar en la página"
        >
          <Search size={24} />
        </button>

        {/* Botón "Ir Arriba" */}
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50"
          title="Volver arriba"
        >
          <ArrowUp size={24} />
        </button>
      </div>

      {/* Buscador completo para Mobile (menor a md) */}
      <div className={`md:hidden fixed bottom-4 right-4 left-4 z-50 flex flex-col items-center gap-3 transition-transform duration-300 ease-in-out ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        <div className="flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden w-full max-w-sm mx-auto">
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
            }}
            placeholder="Buscar en la página..."
            className="flex-1 px-4 py-3 text-sm text-gray-700 outline-none"
            aria-label="Buscar en la página"
          />
          <button
            type="button"
            onClick={handleMobileQuickSearch}
            className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center"
            title="Buscar coincidencia"
          >
            <Search size={20} />
          </button>
        </div>

        {showScrollTop && (
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110"
            title="Volver arriba"
          >
            <ArrowUp size={24} />
          </button>
        )}
      </div>
    </div>
  );
};

export default LogisticsDashboard;
